/**
 * Report markdown renderers.
 *
 * Report copy is markdown end to end: analysts author it through MarkdownField
 * (bold / italic / headings / lists), and the writing agent emits it inside the
 * JSON string fields of authoredSections. Rendering any of those as plain text
 * is what leaks a literal `**` into a customer-facing report, so every authored
 * string renders through one of these two components.
 *
 *   <Md>       block context — paragraphs, lists, headings.
 *   <InlineMd> inline context — inside <li>, <p>, <h3>, a table cell. The
 *              paragraph wrapper is stripped so the markup stays valid and the
 *              surrounding typography keeps control of spacing.
 *
 * These replace the hand-rolled regex helpers (renderBold / renderEm /
 * renderInline) that only understood `*` and `**`, disagreed with each other
 * about which field got which, and mangled `**bold**` wherever the em-only
 * variant ran. Mirrored in swa-axn-customer/src/components/reports/markdown.tsx
 * — the customer magazine has to render identically to the analyst preview and
 * the PDF, and the two SWAs are separate repos.
 */

import type { ReactNode } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';

/** Drop the paragraph wrapper so the output can sit inside inline markup. */
const INLINE_COMPONENTS: Components = {
  p: ({ children }: { children?: ReactNode }) => <>{children}</>,
};

export function InlineMd({ children }: { children?: string | null }) {
  if (!children || !children.trim()) return null;
  return <ReactMarkdown components={INLINE_COMPONENTS}>{children}</ReactMarkdown>;
}

export function Md({
  children,
  className,
}: {
  children?: string | null;
  className?: string;
}) {
  if (!children || !children.trim()) return null;
  return (
    <div className={className}>
      <ReactMarkdown>{children}</ReactMarkdown>
    </div>
  );
}
